import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { VpButton } from '@vtmn-play/react'
import { useContext, useState } from 'react'

import WidgetLoading from 'components/common/cards/widget-loading'
import { RouteId } from 'core/routing/router/route-ids'
import { getPathFromRouteId } from 'core/utils/route-utils'
import Channels from './channels/channels'
import InstoreGmvSegment from './instore-gmv-segment/instore-gmv-segment'
import MissingPreviousYearGmvModal from './missing-previous-year-gmv-modal'
import OverwriteDirectorForecastModal from './overwrite-director-forecast-modal'
import useStoreTrajectoryEditValues from './use-store-trajectory-edit-values'
import { StoreTrajectoryContext } from '../state/store-trajectory-context'
import { useApiDoStoreYearlyForecast } from 'core/api/trajectory-photo/useApiDoStoreYearlyForecast'
import SelectReferenceStoreModal from 'pages/trajectory/store/edit/select-reference-store-modal'
import Alert from 'components/common/alert/alert'

const StoreTrajectoryEdit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    targetYear: forecastYear,
    storeId,
    referenceStoreId,
    setReferenceStoreId,
    referenceStore,
    areForecastValuesValid,
    toAccountValues
  } = useContext(StoreTrajectoryContext)
  const { isLoading, instore, outstore, isReferenceStoreRequired } = useStoreTrajectoryEditValues(referenceStoreId)

  const { mutate, isPending: isSaving } = useApiDoStoreYearlyForecast()
  const [isOverwriteConfirmationOpen, setIsOverwriteConfirmationOpen] = useState(false)

  const goBackToList = () => {
    void navigate(getPathFromRouteId(RouteId.StoreTrajectories))
  }

  const confirmReferenceStore = (storeId: number) => {
    setReferenceStoreId(storeId)
  }

  const openOverwriteConfirmation = () => {
    setIsOverwriteConfirmationOpen(true)
  }

  const closeOverwriteConfirmation = () => {
    if (isSaving) return
    setIsOverwriteConfirmationOpen(false)
  }

  const confirmOverwrite = () => {
    mutate(
      {
        storeId,
        referenceStoreId,
        year: forecastYear,
        accountValues: toAccountValues()
      },
      {
        onSuccess: () => {
          void navigate(getPathFromRouteId(RouteId.StoreTrajectoriesValidate))
        },
        onError: () => {
          setIsOverwriteConfirmationOpen(false)
        }
      }
    )
  }

  if (
    !isLoading &&
    isReferenceStoreRequired &&
    import.meta.env.REACT_APP_TRAJECTORY_REFERENCE_STORE_ENABLED === 'true'
  ) {
    return <SelectReferenceStoreModal onClose={goBackToList} onConfirm={confirmReferenceStore} />
  }

  if (!isLoading && instore?.reference_gmv == null) {
    return <MissingPreviousYearGmvModal onClose={goBackToList} />
  }

  return (
    <div className="mg-store-trajectory" data-testid="store-trajectory-edit">
      <div className="mg-store-trajectory-header">
        <div className="mg-store-trajectory-header-title vp-title-m" data-testid="store-trajectory-edit-title">
          {t('general.trajectory.store.edit.title', { forecastYear })}
        </div>
      </div>
      <div className="mg-store-trajectory-body">
        {isLoading ? (
          <WidgetLoading
            className="mg-store-trajectory-loading"
            dataTestId="store-trajectory-loading"
            skeletonCount={3}
          />
        ) : (
          <div className="mg-store-trajectory-body-content">
            {referenceStoreId ? (
              <Alert
                text={t('general.trajectory.store.reference.info', {
                  referenceStore: `${referenceStore?.id} - ${referenceStore?.label}`
                })}
                icon={{ name: 'information', style: 'COLORED' }}
                severity="info"
                closable={false}
              />
            ) : null}
            <div className="mg-store-trajectory-body-content-sections">
              <Channels instore={instore} outstore={outstore} />

              <InstoreGmvSegment instore={instore} />
            </div>

            <div className="mg-store-trajectory-body-content-buttons">
              <VpButton
                data-testid="store-trajectory-back"
                variant="secondary"
                variantStyle="regular"
                size="medium"
                onClick={goBackToList}
              >
                {t('general.trajectory.store.edit.back')}
              </VpButton>
              <VpButton
                disabled={!areForecastValuesValid() || isSaving}
                data-testid="store-trajectory-edit-save"
                variant="primary"
                variantStyle="regular"
                size="medium"
                onClick={openOverwriteConfirmation}
              >
                {t('general.trajectory.store.edit.save')}
              </VpButton>
            </div>
          </div>
        )}
      </div>
      <OverwriteDirectorForecastModal
        open={isOverwriteConfirmationOpen}
        year={forecastYear}
        isSaving={isSaving}
        onCancel={closeOverwriteConfirmation}
        onConfirm={confirmOverwrite}
      />
    </div>
  )
}

export default StoreTrajectoryEdit
